import React, { useState } from "react";
import PaginationControls from "../../../utils/PaginationControls";
import { PaginationRes } from "../../../models/response.model";
import { IProductionUnit } from "../../../models/factory/productionUnit.model";

interface ProductionUnitsSectionProps {
  productionUnits: IProductionUnit[];
  pagination?: PaginationRes;
  isLoading: boolean;
  onPageChange: (page: number) => void;
}

const formatDate = (value?: Date | string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const formatAmount = (value?: number | null) =>
  typeof value === "number" ? value.toLocaleString() : "-";

const displayValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value !== "object") return String(value);

  const record = value as Record<string, unknown>;
  const namedValue =
    record.productName ||
    record.name ||
    record.warehouseName ||
    record.username ||
    record.email ||
    record._id;

  if (namedValue !== undefined && namedValue !== null) {
    return String(namedValue);
  }

  try {
    return JSON.stringify(value);
  } catch {
    return "-";
  }
};

const Detail = ({
  label,
  value,
  isId = false,
}: {
  label: string;
  value: unknown;
  isId?: boolean;
}) => (
  <div className="min-w-0">
    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
      {label}
    </dt>
    <dd className="mt-1 break-words text-sm text-gray-900">
      {isId ? (
        <span className="inline-flex max-w-full break-all rounded-md border border-gray-200 bg-gray-50 px-2 py-1 font-mono text-xs text-gray-600">
          {displayValue(value)}
        </span>
      ) : (
        displayValue(value)
      )}
    </dd>
  </div>
);

const ProductionUnitsSection: React.FC<ProductionUnitsSectionProps> = ({
  productionUnits,
  pagination,
  isLoading,
  onPageChange,
}) => {
  const [selectedUnit, setSelectedUnit] = useState<IProductionUnit | null>(
    null,
  );
  const totalPages = pagination?.totalPages || 1;
  const totalItems = pagination?.totalCount || 0;

  return (
    <div className="space-y-4">
      <div className="relative min-h-[180px]">
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/75">
            <div className="flex items-center gap-3 text-sm font-medium text-gray-600">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-[#0077C0]" />
              Loading production units...
            </div>
          </div>
        )}

        {!isLoading && productionUnits.length === 0 && (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-12 text-center text-sm text-gray-500">
            No production units found.
          </div>
        )}

        <div className="hidden overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm md:block">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  Serial number
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  Batch number
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  Product
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  Warehouse
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">
                  Total cost
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {productionUnits.map((unit) => (
                <tr
                  key={`desktop-${unit._id}`}
                  tabIndex={0}
                  onClick={() => setSelectedUnit(unit)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedUnit(unit);
                    }
                  }}
                  className="cursor-pointer hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                >
                  <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-gray-900">
                    {displayValue(unit.serialNumber)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                    {displayValue(unit.batchNo)}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900">
                    {unit.productId.productName}
                    <span className="mt-1 block text-xs text-gray-500">
                      {unit.productId._id}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900">
                    {unit.warehouseId.name}
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-right text-sm font-semibold text-gray-900">
                    {formatAmount(unit.totalCost)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-right text-sm font-semibold text-gray-900">
                    {formatAmount(unit.unitTotalPriceIncludingEverything)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 md:hidden">
          {productionUnits.map((unit) => (
            <button
              key={`${unit.requestId}-${unit.serialNumber}`}
              type="button"
              onClick={() => setSelectedUnit(unit)}
              className="rounded-lg border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:border-[#0077C0] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#0077C0]"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900">
                    {displayValue(unit.serialNumber) === "-"
                      ? "Unnamed unit"
                      : displayValue(unit.serialNumber)}
                  </p>
                  <p className="mt-1 truncate text-xs text-gray-500">
                    Batch: {displayValue(unit.batchNo)}
                  </p>
                </div>
                <span className="shrink-0 rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-[#0077C0]">
                  View details
                </span>
              </div>
              <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3">
                <Detail label="Product" value={unit.productId.productName} />
                <Detail label="Warehouse" value={unit.warehouseId.name} />
                <Detail
                  label="Manufacturing SN"
                  value={unit.manufacturingSerialNumber}
                />
                <Detail
                  label="Total cost"
                  value={formatAmount(unit.totalCost)}
                />
                <Detail
                  label="Total"
                  value={formatAmount(unit.unitTotalPriceIncludingEverything)}
                />
              </dl>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white">
        <PaginationControls
          currentPage={pagination?.page || 1}
          totalPages={totalPages}
          onPageChange={onPageChange}
          isLoading={isLoading}
          totalItems={totalItems}
        />
      </div>

      {selectedUnit && (
        <div
          className="fixed inset-0 z-[110] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="production-unit-details-title"
          onClick={() => setSelectedUnit(null)}
        >
          <div
            className="flex max-h-[95vh] w-full flex-col overflow-y-auto rounded-t-2xl bg-white shadow-2xl sm:max-w-3xl sm:rounded-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-200 bg-white/95 px-5 py-4 backdrop-blur sm:px-6">
              <div>
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-[#0077C0]" />
                  <h2
                    id="production-unit-details-title"
                    className="text-lg font-semibold tracking-tight text-gray-900"
                  >
                    Production unit details ·{" "}
                    {displayValue(selectedUnit.productId.productName)}
                  </h2>
                </div>
                <p className="mt-1 break-all text-sm text-gray-500">
                  {displayValue(selectedUnit.serialNumber) !== "-"
                    ? displayValue(selectedUnit.serialNumber)
                    : displayValue(selectedUnit.requestId)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedUnit(null)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 text-xl leading-none text-gray-500 transition hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900"
                aria-label="Close details"
              >
                ×
              </button>
            </div>

            <dl className="grid grid-cols-1 gap-x-6 gap-y-5 px-5 py-6 sm:grid-cols-2 sm:px-6">
              <Detail label="Record ID" value={selectedUnit._id} isId />
              <Detail label="Request ID" value={selectedUnit.requestId} isId />
              <Detail label="Batch number" value={selectedUnit.batchNo} />
              <Detail
                label="Product"
                value={selectedUnit.productId.productName}
              />
              <Detail
                label="Product ID"
                value={selectedUnit.productId._id}
                isId
              />
              <Detail label="Warehouse" value={selectedUnit.warehouseId.name} />
              <Detail
                label="Warehouse ID"
                value={selectedUnit.warehouseId._id}
                isId
              />
              <Detail label="Serial number" value={selectedUnit.serialNumber} />
              <Detail
                label="Logger serial number"
                value={selectedUnit.loggerSerialNumber}
              />
              <Detail
                label="Manufacturing serial number"
                value={selectedUnit.manufacturingSerialNumber}
              />
              <Detail
                label="Created by"
                value={selectedUnit.createdBy.username}
              />
              <Detail
                label="Created by ID"
                value={selectedUnit.createdBy._id}
                isId
              />
              <Detail
                label="Created at"
                value={formatDate(selectedUnit.createdAt)}
              />
              <Detail
                label="Updated at"
                value={formatDate(selectedUnit.updatedAt)}
              />
              <Detail
                label="Dispatched date"
                value={formatDate(selectedUnit.dispatchedDate)}
              />
              <Detail
                label="Material cost"
                value={formatAmount(selectedUnit.totalMaterialCost)}
              />
              <Detail
                label="Travel cost"
                value={formatAmount(selectedUnit.totalTravelCost)}
              />
              <Detail
                label="Transfer cost"
                value={formatAmount(selectedUnit.totalTransferCost)}
              />
              <Detail
                label="Other cost"
                value={formatAmount(selectedUnit.totalOtherCost)}
              />
              <Detail
                label="Labour cost"
                value={formatAmount(selectedUnit.labourCost)}
              />
              <Detail
                label="Misc. cost %"
                value={selectedUnit.miscCostPercent}
              />
              <Detail
                label="Total cost"
                value={formatAmount(selectedUnit.totalCost)}
              />
              <Detail
                label="Total price"
                value={formatAmount(
                  selectedUnit.unitTotalPriceIncludingEverything,
                )}
              />
            </dl>

            <div className="border-t border-gray-200 bg-gray-50/70 px-5 py-6 sm:px-6">
              <div className="flex items-end justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#0077C0]">
                    Consumption
                  </p>
                </div>
                <span className="text-xs text-gray-500">
                  {selectedUnit.materialsUsed?.length || 0} item
                  {selectedUnit.materialsUsed?.length === 1 ? "" : "s"}
                </span>
              </div>
              {selectedUnit.materialsUsed?.length ? (
                <div className="mt-4 space-y-3">
                  {selectedUnit.materialsUsed.map((material, index) => (
                    <div
                      key={`${material.requestItemId}-${index}`}
                      className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                    >
                      <div className="mb-4 flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
                        <div className="min-w-0">
                          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            Raw material
                          </p>
                          <p className="mt-1 break-words text-sm font-semibold text-gray-900">
                            {displayValue(material.rawMaterialName)}
                          </p>
                        </div>
                        <span className="shrink-0 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-[#0077C0]">
                          #{index + 1}
                        </span>
                      </div>
                      <dl className="grid grid-cols-1 gap-4 min-[420px]:grid-cols-2 sm:grid-cols-4">
                        <Detail
                          label="Quantity"
                          value={material.quantityUsed}
                        />
                        <Detail
                          label="Material cost"
                          value={formatAmount(material.materialCost)}
                        />
                        <Detail
                          label="Total cost"
                          value={formatAmount(material.totalCost)}
                        />
                        <Detail
                          label="Request item"
                          value={material.requestItemId}
                          isId
                        />
                        <Detail
                          label="Store inward"
                          value={material.storeInwardId}
                          isId
                        />
                        <Detail
                          label="Travel cost"
                          value={formatAmount(material.travelCost)}
                        />
                        <Detail
                          label="Other cost"
                          value={formatAmount(material.otherCost)}
                        />
                      </dl>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-sm text-gray-500">
                  No materials recorded.
                </p>
              )}
            </div>

            <div className="border-t border-gray-200 px-5 py-6 sm:px-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#0077C0]">
                Movement
              </p>
              <h3 className="mt-1 text-base font-semibold text-gray-900">
                Transfer history
              </h3>
              {selectedUnit.transferHistory?.length ? (
                <ul className="mt-4 space-y-2 text-sm text-gray-700">
                  {selectedUnit.transferHistory.map((entry, index) => (
                    <li
                      key={`${entry}-${index}`}
                      className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3"
                    >
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-[#0077C0] ring-1 ring-gray-200">
                        {index + 1}
                      </span>
                      {displayValue(entry)}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-sm text-gray-500">
                  No transfer history recorded.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionUnitsSection;
